import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmPlanOption } from '../config/crmPlans';
import { usdToTzs } from '../config/crmPlans';
import { formatPlanPrice } from '../config/unifiedPlans';
import { normalizeUiLang } from './localePreference';

export type CrmPlanI18nSlug = 'small' | 'medium' | 'large' | 'enterprise';

/** Map canonical CRM plan id to i18n key prefix (crm_plan_{slug}_*). */
export function getCrmPlanI18nSlug(planId: string): CrmPlanI18nSlug | null {
  const slug = String(planId || '').replace(/^crm_/, '').toLowerCase();
  if (slug === 'small' || slug === 'medium' || slug === 'large' || slug === 'enterprise') {
    return slug;
  }
  return null;
}

/** Localize CRM subscription packages using crm_plan_{slug}_* keys. */
export function useLocalizedCrmPlans(plans: CrmPlanOption[]) {
  const { t, i18n } = useTranslation();
  const lang = normalizeUiLang(i18n.resolvedLanguage || i18n.language);

  const txtList = useCallback(
    (key: string, fallback: string[]): string[] => {
      const fromI18n = t(key, { returnObjects: true });
      if (Array.isArray(fromI18n) && fromI18n.length > 0) return fromI18n as string[];
      return fallback;
    },
    [t],
  );

  return useMemo(() => {
    return plans.map((plan) => {
      const slug = getCrmPlanI18nSlug(plan.planId);
      if (!slug) return plan;
      const prefix = `crm_plan_${slug}`;
      const priceUsd = plan.priceUsd || 0;
      const price =
        lang === 'sw' && priceUsd > 0
          ? formatPlanPrice(usdToTzs(priceUsd), 'TZS')
          : priceUsd > 0
            ? formatPlanPrice(priceUsd, 'USD')
            : plan.price;
      return {
        ...plan,
        name: t(`${prefix}_name`, plan.name),
        description: t(`${prefix}_desc`, plan.description),
        features: txtList(`${prefix}_features`, plan.features),
        price,
      };
    });
  }, [plans, t, txtList, lang]);
}
